import { isEmpty } from 'lodash';
import Vue from 'vue';
import i18n from '@/plugins/i18n';
import { captureUnexpectedException } from '@/common/sentry';

export const helpers = {
  getUserGeoPermission() {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: Infinity,
          maximumAge: 400,
        });
        navigator.permissions.query({ name: 'geolocation' }).then((result) => {
          if (result.state === 'prompt') {
            const newWindow = window.open();
            newWindow.close();
          }
        });
      } else {
        console.warn('Geolocation is not supported by this browser');
      }
    });
  },

  getUserPosition(geo, countryOptions = []) {
    const lat = geo.coords.latitude,
      lng = geo.coords.longitude,
      latlng = new google.maps.LatLng(lat, lng),
      geocoder = new google.maps.Geocoder();

    return new Promise((resolve, reject) => {
      geocoder.geocode({ latLng: latlng }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK) {
          if (!isEmpty(results)) {
            const countryResult = results.find(res => res.types[0] === 'country') || null;
            const country = countryResult.address_components[0].long_name;
            const isCountryAvailable = countryOptions.some(el => el === country);

            if (isCountryAvailable) {
              const stateResult = results.find(res => res.types[0] === 'administrative_area_level_1') || null;
              const state = stateResult.address_components[0].short_name;

              resolve({ country, state });
            } else {
              resolve();
            }
          } else {
            console.warn('No reverse geocode results.');
            resolve();
          }
        } else {
          console.error('Geocoder failed: ', status);
          reject();
        }
      });
    });
  },
  warnMessageHandler(err) {
    let warnMessage = this.errorDecoder(err);
    if (warnMessage instanceof Error) {
      warnMessage = warnMessage.message;
    }

    Vue.prototype.$notifyWarn(warnMessage);
    console.warn('[warnMessageHandler]: ', warnMessage);
  },
  errorMessageHandler(err) {
    const errorMessage = this.errorDecoder(err);
    Vue.prototype.$notifyError(errorMessage);
    captureUnexpectedException(err, err?.config?.headers?.['X-Request-Id']);
    console.error('[errorMessageHandler]: ', errorMessage);
  },
  errorDecoder(err, message = '') {
    console.warn('errorDecoder: ', err);
    if (!err?.response) {
      console.warn('err:', err);
      return err;
    }
    const errServerMessage = err.response.data.errorMessage;
    console.warn('Error Server Message: ', errServerMessage);

    let showMsg;
    if (typeof errServerMessage === 'string') {
      showMsg = errServerMessage;
    } else if (Object.prototype.hasOwnProperty.call(errServerMessage, 'errorId')) {
      console.log(errServerMessage, '<<<<');
      const preparedArgs = (errServerMessage.args || []).reduce((acc, val, idx) => ({ ...acc, [`arg${idx + 1}`]: val }), {});
      const translationKey = `backendTranslations.${errServerMessage.errorId}`;
      showMsg = i18n.t(translationKey, preparedArgs);
      if (showMsg === translationKey && errServerMessage.errorMessage) {
        showMsg = errServerMessage.errorMessage;
      }
    } else if (Object.prototype.hasOwnProperty.call(errServerMessage, 'systemError')) {
      showMsg = errServerMessage.systemError;
    } else if (Object.prototype.hasOwnProperty.call(errServerMessage, 'systemErrors')) {
      showMsg = errServerMessage.systemErrors.join('. ');
    } else if (Object.prototype.hasOwnProperty.call(errServerMessage, 'message') && errServerMessage.message.errorId) {
      showMsg = i18n.t(`backendTranslations.${errServerMessage.message.errorId}`);
    } else if (err.response.status === 403) {
      showMsg = i18n.t('notifications.noPermission');
    } else {
      showMsg = message.length ? `${message}. ${err.message}` : err.message;
    }

    if (showMsg === 'jwt malformed') {
      showMsg = '';
    }
    return showMsg;
  },

  timezoneForBackend(timezoneStr) {
    let fixedTimezone = timezoneStr;

    fixedTimezone = fixedTimezone.replace(/\.5/, ':30');
    fixedTimezone = fixedTimezone.replace(/\.75/, ':45');

    return fixedTimezone;
  },
};

export function downloadFile({ content, type }, fileName) {
  const hiddenElement = document.createElement('a');
  hiddenElement.href = window.URL.createObjectURL(new Blob([content], { type }));
  hiddenElement.target = '_blank';
  hiddenElement.download = fileName;
  hiddenElement.click();
}

// FILTERS
export function upperFirst(value) {
  if (!value) return '';
  value = value.toString();
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}

export function lowerCase(value) {
  if (!value) return '';
  value = value.toString();
  return value.toLowerCase();
}

export function upperCase(value) {
  if (!value) return '';
  value = value.toString();
  return value.toUpperCase();
}

export function saveFile(blob, filename, type) {
  // It is necessary to create a new blob object with mime-type explicitly set
  // otherwise only Chrome works like it should
  const newBlob = new Blob([blob], { type });

  // IE doesn't allow using a blob object directly as link href
  // instead it is necessary to use msSaveOrOpenBlob
  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(newBlob);
    return;
  }

  // For other browsers:
  // Create a link pointing to the ObjectURL containing the blob.
  const data = window.URL.createObjectURL(newBlob);
  const link = document.createElement('a');
  link.href = data;
  link.download = filename;
  link.click();
  let timeout = setTimeout(() => {
    // For Firefox it is necessary to delay revoking the ObjectURL
    window.URL.revokeObjectURL(data);
    clearTimeout(timeout);
    timeout = null;
  }, 100);
}

export default helpers;
